import { ChangeDetectionStrategy, Component, Inject, signal, Signal, viewChild, WritableSignal } from '@angular/core';
import { CreatePaymentMethodPayload, CreatePaymentMethodResponse } from '@core/models/entities/payment-method.models.';
import { GusDialogCloseEvent, GusDialogConfig } from '@shared/modules/dialog/models/dialog.models';
import { GusPaymentMethodForm } from '@shared/modules/payment-method/models/payment-method.models';
import { BehaviorSubject, combineLatest, exhaustMap, finalize, first, Observable } from 'rxjs';
import {
  CreateSubscriptionPayload,
  PlatformSubscription,
  Subscription,
} from '@core/models/entities/subscription.model';
import { SnackbarApiService } from '@core/services/helpers/snackbar-api.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PaymentMethodApi } from '@core/services/api/payment-method.api';
import { SubscriptionApi } from '@core/services/api/subscription.api';
import { toObservable } from '@angular/core/rxjs-interop';
import { PaymentMethodResult } from '@stripe/stripe-js';

@Component({
  selector: 'gus-collection-subscription-dialog',
  templateUrl: './collection-subscription-dialog.component.html',
  styleUrl: './collection-subscription-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionSubscriptionDialogComponent {
  protected subscription!: Subscription;

  protected loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  paymentMethodForm: Signal<GusPaymentMethodForm> = viewChild.required(GusPaymentMethodForm);

  selectedPlatformSubscription!: PlatformSubscription;

  enableDisclaimer: WritableSignal<boolean> = signal(false);

  constructor(
    private snackbarApiService: SnackbarApiService,
    private subscriptionApi: SubscriptionApi,
    private paymentMethodApi: PaymentMethodApi,
    private dialogRef: MatDialogRef<CollectionSubscriptionDialogComponent, GusDialogCloseEvent>,
    @Inject(MAT_DIALOG_DATA) public config: GusDialogConfig<Subscription>
  ) {
    this.subscription = config.data!;

    this.selectedPlatformSubscription = this.subscription.web[0];

    if (this.subscription.group_name.includes('Goomi Unlimited')) {
      this.enableDisclaimer.set(true);
    }

    combineLatest([toObservable(this.paymentMethodForm), this.dialogRef.afterOpened()])
      .pipe(first())
      .subscribe(() => this.paymentMethodForm().initializePaymentDetails());
  }

  submit(): void {
    this.loading$.next(true);

    this.paymentMethodForm()
      .createMethod()
      .pipe(exhaustMap((result: PaymentMethodResult) => this.createPaymentMethod(result)))
      .pipe(exhaustMap((method: CreatePaymentMethodResponse) => this.subscribeToCollection(method)))
      .pipe(finalize(() => this.loading$.next(false)))
      .subscribe({
        next: () => {
          this.dialogRef.close({ primaryButtonClicked: true });
          this.snackbarApiService.success('You have successfully subscribed');
        },
        error: err => {
          console.log(err);
          // this.snackbarApiService.failure(err?.message ?? 'An error occurred');
        },
      });
  }

  private createPaymentMethod(result: PaymentMethodResult): Observable<CreatePaymentMethodResponse> {
    const payload: CreatePaymentMethodPayload = {
      stripe_source_id: result.paymentMethod!.id,
      last_4_digits: result.paymentMethod!.card!.last4,
      account_name: result.paymentMethod!.card!.brand,
      is_default: true,
      type: 'cc',
    };

    return this.paymentMethodApi.create(payload);
  }

  private subscribeToCollection(method: CreatePaymentMethodResponse): Observable<Subscription> {
    const payload: CreateSubscriptionPayload = {
      subscription: this.subscription.id,
      payment_method: method.id,
      product_id: this.selectedPlatformSubscription.product_id,
      price_id: this.selectedPlatformSubscription.price_id,
    };

    return this.subscriptionApi.create(payload);
  }

  close(): void {
    this.dialogRef.close({ secondaryButtonClicked: true });
  }
}
